import React, { useEffect, useState } from "react";
import { Alert, Box, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import CollapsiblePanel from "./CollapsiblePanel";
import History from "./History";
import Parameters from "./Parameters";
import Splines from "./Splines";
import DataInput from "./DataInput";
import { getInitialState } from "./DefaultParameters";

const createEmptyCalRows = (count = 5) =>
    Array.from({ length: count }, () => ({ sav: "", resp1: "", resp2: "" }));

// DM (Dual Wavelength, eAssayModelType=6) and DM_BLANK (=7) feed every
// averaged response through the G1/G2 splines BEFORE the A0..A3 fit (see
// CP_CMeasurementDrySlideDM::CP_CalculateCalParams in
// vitros-pure/mathlib/.../CP_CMeasurementDrySlideDM.cc). With zero-node
// splines the very first CP_InterpolateFromSpline call sets
// CPEC_OUTSIDE_SPLINE_RANGE_HIGH_ERROR (0x20) and the math returns
// U90-021 (CP_DIVIDE_BY_ZERO_11_ERR) with A0..A3 = 0.
const REQUIRES_G_SPLINES = (model) => {
    const m = Number(model);
    return m === 6 || m === 7; // DM, DM_BLANK
};

// A successful DM calibration is bABFCalibrated == 1 AND non-zero slope.
// All zeros + a U90 condition code is the signature of the math failing
// out before CP_CalibrateKModel ever ran.
const isFailedCalibration = (data) => {
    if (!data) return true;
    const a0 = Number(data.fCalInterceptA0) || 0;
    const a1 = Number(data.fCalSlopeA1) || 0;
    const a2 = Number(data.fCalCurvatureA2) || 0;
    const a3 = Number(data.fCalBlankCorrectionFactorA3) || 0;
    const allZero = a0 === 0 && a1 === 0 && a2 === 0 && a3 === 0;
    const cc = String(data.zConditionCode || "");
    const err = String(data.zError || "");
    const hasErrorBits = err && !/^U93:0+:0+$/.test(err);
    return allZero && (cc.startsWith("U") || hasErrorBits || data.bABFCalibrated === 0);
};

// Calibration tab. Calls POST /api/v1/microslide/calibrate.
export default function CalibrationLayout() {
    const [resultsCollapsed, setResultsCollapsed] = useState(true);
    const [dataCollapsed, setDataCollapsed] = useState(false);
    const [splinesCollapsed, setSplinesCollapsed] = useState(true);
    const [paramsCollapsed, setParamsCollapsed] = useState(true);

    // One row per calibration level; resp1/resp2 hold replicate values
    // (comma- or space-separated) to match the legacy
    // MSDualWavelengthLevel { SAV, Resp1[], Resp2[] } shape.
    const [rows, setRows] = useState(createEmptyCalRows(5));
    const [parameters, setParameters] = useState(getInitialState());
    const [splines, setSplines] = useState({
        preprocessing: null, g1: null, g2: null, g3: null, g4: null
    });

    const [splineWarning, setSplineWarning] = useState(null);
    const [calFailureMsg, setCalFailureMsg] = useState(null);

    const navigate = useNavigate();

    const [resultDataMap, setResultDataMap] = useState(() => {
        const saved = sessionStorage.getItem("microslideHistory_calibrate");
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        if (resultDataMap && Object.keys(resultDataMap).length > 0) {
            setResultsCollapsed(false);
        }
    }, [resultDataMap]);

    // Triggered by Header on a successful /calibrate response.  Pulls A0-A3
    // out of the payload, persists them in localStorage so the Prediction
    // tab can recover them across refreshes, and navigates to /microslide
    // with route state so NavigationTabs auto-opens the Prediction tab.
    const handleCalibrationResult = (data) => {
        if (!data) return;
        const a0 = Number(data.fCalInterceptA0);
        const a1 = Number(data.fCalSlopeA1);
        const a2 = Number(data.fCalCurvatureA2);
        const a3 = Number(data.fCalBlankCorrectionFactorA3);
        if (![a0, a1, a2, a3].every(Number.isFinite)) return;

        // Hard stop: don't auto-apply an obviously failed calibration to
        // Predict (that's what produces the flat A0..A3 = 0 chart). Show
        // the math's own condition / error code so the user can act.
        if (isFailedCalibration(data)) {
            const cc = data.zConditionCode || "(no condition code)";
            const err = data.zError || "";
            const needsSplines = REQUIRES_G_SPLINES(parameters?.eAssayModelType)
                && (!splines?.g1 || !splines.g1.nodes?.length);
            setCalFailureMsg(
                `Calibration failed: ${cc}${err ? " / " + err : ""}. ` +
                (needsSplines
                    ? "DM (Dual Wavelength) requires G1 (and G2 when K-exponent != 0) splines. " +
                      "Open the Splines panel and load nodes for G1/G2 before re-running."
                    : "A0..A3 came back as zero, so the result is NOT being auto-applied to Predict.")
            );
            // Open the Splines panel so the fix is one click away.
            setSplinesCollapsed(false);
            return;
        }

        const calibrationResponse = {
            fCalInterceptA0: a0,
            fCalSlopeA1: a1,
            fCalCurvatureA2: a2,
            fCalBlankCorrectionFactorA3: a3,
            bABFCalibrated: data.bABFCalibrated ?? 1,
            zConditionCode: data.zConditionCode ?? "",
            // Tracked so the Prediction tab can warn if the user changes the
            // assay model after a calibration is active.
            eAssayModelType: parameters?.eAssayModelType
        };

        localStorage.setItem(
            "microslideCalibrationResponse",
            JSON.stringify(calibrationResponse)
        );

        // The Prediction tab pushes its samples' Resp1 / Resp2 through the
        // same G1 / G2 splines the Calibrator used; without them the math
        // returns U90-289 / spline-out-of-range and dConcentration = null.
        // Persist the splines that produced this calibration so PredictionLayout
        // can hydrate them automatically.
        localStorage.setItem(
            "microslideCalibrationSplines",
            JSON.stringify(splines || {})
        );

        navigate("/microslide", {
            state: {
                openPredictionTab: true,
                calibrationResponse,
                calibrationSplines: splines
            }
        });
    };

    return (
        <div>
            <Header
                mode="calibrate"
                rows={rows}
                parameters={parameters}
                setParameters={setParameters}
                extras={{ splines }}
                setResultDataMap={setResultDataMap}
                onRun={() => {
                    setResultsCollapsed(false);
                    setCalFailureMsg(null);
                    if (REQUIRES_G_SPLINES(parameters?.eAssayModelType)
                        && (!splines?.g1 || !splines.g1.nodes?.length)) {
                        setSplineWarning(
                            "DM (Dual Wavelength) needs G1 spline nodes to convert " +
                            "responses before fitting A0..A3. Without them the math " +
                            "returns U90-021 / spline-out-of-range and A0..A3 = 0. " +
                            "Open the Splines panel to add nodes."
                        );
                        setSplinesCollapsed(false);
                    } else {
                        setSplineWarning(null);
                    }
                }}
                onResult={handleCalibrationResult}
            />

            {splineWarning && (
                <Box px={2} pt={1}>
                    <Alert severity="warning" onClose={() => setSplineWarning(null)}>
                        {splineWarning}
                    </Alert>
                </Box>
            )}

            <Snackbar
                open={!!calFailureMsg}
                autoHideDuration={10000}
                onClose={() => setCalFailureMsg(null)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={() => setCalFailureMsg(null)}
                    sx={{ width: "100%" }}
                >
                    {calFailureMsg}
                </Alert>
            </Snackbar>

            <Box display="flex" flexDirection="column" padding={2}>
                <CollapsiblePanel
                    title="Results"
                    collapsed={resultsCollapsed}
                    onToggle={() => setResultsCollapsed(!resultsCollapsed)}
                >
                    <History
                        resultDataMap={resultDataMap}
                        resultFields={["A0", "A1", "A2", "A3", "Calibrated", "ConditionCode", "Error"]}
                    />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Calibration Data (one row per level)"
                    collapsed={dataCollapsed}
                    onToggle={() => setDataCollapsed(!dataCollapsed)}
                >
                    <DataInput
                        columns={[
                            { key: "sav", label: "SAV" },
                            { key: "resp1", label: "Response 1 reps" },
                            { key: "resp2", label: "Response 2 reps" }
                        ]}
                        rows={rows}
                        setRows={setRows}
                        placeholder={"SAV\tResp1 (comma sep)\tResp2 (comma sep)\n0\t0.10, 0.11\t0.05, 0.06\n10\t0.50, 0.52\t0.25, 0.26\n..."}
                    />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Splines"
                    collapsed={splinesCollapsed}
                    onToggle={() => setSplinesCollapsed(!splinesCollapsed)}
                >
                    <Splines splines={splines} setSplines={setSplines} />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Assay Parameters"
                    collapsed={paramsCollapsed}
                    onToggle={() => setParamsCollapsed(!paramsCollapsed)}
                >
                    <Parameters parameters={parameters} setParameters={setParameters} />
                </CollapsiblePanel>
            </Box>
        </div>
    );
}
